import React, { useEffect, useState } from "react";
import {
  getSingleProductAcc,
  getSingleProductAccBySlug,
} from "../api/apiCalls";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import OrderProduct from "./OrderProduct";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import {
  createBreadcrumbSchema,
  createProductSchema,
  usePageSeo,
} from "../utils/seo";
import { getAccessoryUrl, getProductSlug } from "../utils/productUrl";

SwiperCore.use([Navigation, Thumbs]);

function SingleProductAcc() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [showAdded, setShowAdded] = useState(false);
  const { productId, slug } = useParams();
  const navigate = useNavigate();
  const isLegacyUrl = Boolean(productId);
  const canonicalPath = product
    ? getAccessoryUrl(product)
    : `/dodatok/${slug || ""}`;

  usePageSeo({
    title: product ? `${product.title} | Buzz Mobile` : "Галантерија | Buzz Mobile",
    description: product
      ? `Купете ${product.title} од Buzz Mobile.`
      : "Погледнете ја мобилната галантерија од Buzz Mobile.",
    canonicalPath,
    image: product?.img?.[0],
    type: "product",
    structuredData: product
      ? [
          createProductSchema({ product, productPath: canonicalPath }),
          createBreadcrumbSchema([
            { name: "Buzz Mobile", path: "/" },
            {
              name: "Галантерија",
              path: `/galanterija/${product.categories?.[0] || "polnaci"}`,
            },
            { name: product.title, path: canonicalPath },
          ]),
        ]
      : null,
  });

  useEffect(() => {
    let isMounted = true;

    window.scrollTo(0, 0);
    setLoading(true);
    setProduct(null);

    const productRequest = isLegacyUrl
      ? getSingleProductAcc(productId)
      : getSingleProductAccBySlug(slug);

    productRequest.then(
      (res) => {
        if (!isMounted) {
          return;
        }

        setProduct(res || null);
        setLoading(false);
      },
      (err) => {
        console.log(err);
        if (isMounted) {
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
    };
  }, [isLegacyUrl, productId, slug]);

  useEffect(() => {
    if (!product) {
      return;
    }

    const productPath = getAccessoryUrl(product);

    if (isLegacyUrl || slug !== getProductSlug(product)) {
      navigate(productPath, { replace: true });
    }
  }, [isLegacyUrl, navigate, product, slug]);

  const handleAddToCart = () => {
    if (!product) {
      return;
    }

    let current = sessionStorage.getItem("cart");
    current = current ? JSON.parse(current) : [];

    const item = {
      id: product._id,
      title: product.title,
      slug: product.slug,
      price: product.price,
      img: product.img?.[0],
      categories: product.categories?.[0] || "accessories",
      quantity: 1,
    };

    const sameItemIndex = current.findIndex(
      (entry) => entry.id === item.id && !entry.storage && !entry.color
    );

    if (sameItemIndex >= 0) {
      current[sameItemIndex].quantity += 1;
    } else {
      current.push(item);
    }

    sessionStorage.setItem("cart", JSON.stringify(current));
    setShowAdded(true);
    setTimeout(() => setShowAdded(false), 2000);
  };

  if (loading) {
    return (
      <div className="single__product__container container">
        <LinearProgress />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="single__product__container container">
        <div className="single__product__info-card">
          <p className="sp__title">Производот не е достапен.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="single__product__container container">
      <div className="single__product__wrapper">
        <section className="single__product__media-card">
          <div className="single__product__media-copy">
            <span className="single__product__eyebrow">{product.brand}</span>
          </div>

          <div className="single__product__images">
            <Swiper
              style={{
                "--swiper-navigation-color": "#1e1e29",
                "--swiper-pagination-color": "#1e1e29",
                width: "100%",
              }}
              spaceBetween={10}
              navigation={true}
              thumbs={{
                swiper:
                  thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              modules={[Navigation, Thumbs]}
              className="single__product__gallery"
            >
              {product.img?.map((item, index) => (
                <SwiperSlide key={index}>
                  <img src={item} alt={product.title} />
                </SwiperSlide>
              ))}
            </Swiper>

            <Swiper
              onSwiper={setThumbsSwiper}
              slidesPerView={3}
              spaceBetween={12}
              modules={[Navigation, Thumbs]}
              className="single__product__thumbs"
              style={{ width: "100%" }}
            >
              {product.img?.map((item, index) => (
                <SwiperSlide key={index}>
                  <img src={item} alt={product.title} height={100} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        <section className="single__product__info-card">
          <div className="single__product__hero">
            <div className="single__product__title">
              <p className="sp__title">{product.title}</p>
            </div>
          </div>

          <div className="single__product__purchase">
            <div className="single__product__summary">
              <div className="single__product__price">
                <p className="sp__price">
                  {parseFloat(product.price).toLocaleString("en")} <span>ден.</span>
                </p>
              </div>
            </div>

            <div className="single__product__actions">
              <button
                type="button"
                className="single__product__btn single__product__btn--secondary"
                onClick={handleAddToCart}
              >
                <AddShoppingCartIcon fontSize="small" />
                <span>Додај во кошничка</span>
              </button>

              <OrderProduct
                title={product.title}
                price={product.price}
                img={product.img?.[0]}
              />
            </div>
          </div>
        </section>
      </div>

      {showAdded ? (
        <div className="addtocart__popup">
          <Stack sx={{ width: "100%" }} spacing={2}>
            <Alert severity="success">
              <p>
                {product.title} е додаден во{" "}
                <Link to="/cart" className="links">
                  кошничката
                </Link>
              </p>
            </Alert>
          </Stack>
        </div>
      ) : null}
    </div>
  );
}

export default SingleProductAcc;
